if (!customElements.get('back-in-stock-signup')) {
  customElements.define(
    'back-in-stock-signup',
    class BackInStockSignup extends HTMLElement {
      #variantChangeUnsubscriber;
      #onSubmitBound;
      #onEmailKeydownBound;

      connectedCallback() {
        if (!this.dataset.companyId) return;

        this.form = this.querySelector('[data-back-in-stock-form]');
        this.submitButton = this.querySelector('[data-back-in-stock-submit]');
        this.successMessage = this.querySelector('[data-back-in-stock-success]');
        this.errorMessage = this.querySelector('[data-back-in-stock-error]');
        this.emailInput = this.querySelector('[data-back-in-stock-email]');
        this.variants = this.#parseVariants();
        this.#onSubmitBound = this.#onSubmit.bind(this);
        this.#onEmailKeydownBound = this.#onEmailKeydown.bind(this);

        if (this.form?.tagName === 'FORM') {
          this.form.addEventListener('submit', this.#onSubmitBound);
        } else {
          // Compact card markup uses a div so it can sit inside the product form.
          this.submitButton?.addEventListener('click', this.#onSubmitBound);
          this.emailInput?.addEventListener('keydown', this.#onEmailKeydownBound);
        }

        if (this.classList.contains('back-in-stock-signup--compact')) {
          // Product cards sync from their own form, not page-wide variant pubsub.
          this.#bindCardVariantListeners();
        } else if (!this.dataset.sectionId) {
          this.#variantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
            this.#onVariantChange.bind(this)
          );
          this.#bindCardVariantListeners();
        }

        this.#syncVisibility(this.#getInitialVariant());
      }

      disconnectedCallback() {
        this.#variantChangeUnsubscriber?.();
        this.form?.removeEventListener('submit', this.#onSubmitBound);
        this.submitButton?.removeEventListener('click', this.#onSubmitBound);
        this.emailInput?.removeEventListener('keydown', this.#onEmailKeydownBound);
      }

      #onEmailKeydown(event) {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        this.#onSubmit(event);
      }

      #parseVariants() {
        const jsonNode = this.querySelector('[data-back-in-stock-variants]');
        if (!jsonNode?.textContent) return [];

        try {
          return JSON.parse(jsonNode.textContent);
        } catch {
          return [];
        }
      }

      #bindCardVariantListeners() {
        const cardForm =
          this.closest('form[data-type="add-to-cart-form"]') ||
          this.closest('.product-card__form')?.querySelector('form[data-type="add-to-cart-form"]');
        if (!cardForm) return;

        cardForm.addEventListener('change', () => {
          this.#syncVisibility(this.#resolveVariantFromForm(cardForm));
        });
      }

      #resolveVariantFromForm(form) {
        const variantInput = form.querySelector('select.product-variant-id, input.product-variant-id');
        if (variantInput?.value) {
          return this.variants.find((entry) => String(entry.id) === String(variantInput.value));
        }

        const optionInputs = form.querySelectorAll('[name^="options["]:checked, select[name^="options["]');
        if (!optionInputs.length) return null;

        const selectedOptions = Array.from(optionInputs).map((input) => {
          return input.tagName === 'SELECT' ? input.value : input.value;
        });

        return this.variants.find((entry) => {
          const variantOptions = [entry.option1, entry.option2, entry.option3].filter(Boolean);
          if (variantOptions.length !== selectedOptions.length) return false;
          return variantOptions.every((option, index) => option === selectedOptions[index]);
        });
      }

      #onVariantChange({ data: { variant } }) {
        if (this.dataset.sectionId) return;
        this.#syncVisibility(variant);
      }

      #getInitialVariant() {
        const variantId = this.dataset.variantId;
        if (!variantId) return null;

        return (
          this.variants.find((entry) => String(entry.id) === String(variantId)) || {
            id: variantId,
            available: this.dataset.initiallyVisible !== 'true',
          }
        );
      }

      #syncVisibility(variant) {
        const allowSignup = this.dataset.allowSignup === 'true';
        if (!allowSignup || !variant) {
          this.hidden = true;
          this.#toggleCardActions(true, variant);
          return;
        }

        this.dataset.variantId = variant.id;
        const shouldShow = variant.available === false;
        this.hidden = !shouldShow;
        this.#toggleCardActions(!shouldShow, variant);
      }

      #toggleCardActions(showActions, variant) {
        const cardForm =
          this.closest('form[data-type="add-to-cart-form"]') ||
          this.closest('.product-card__form')?.querySelector('form[data-type="add-to-cart-form"]');
        if (!cardForm) return;

        cardForm.querySelectorAll('[data-back-in-stock-toggle]').forEach((element) => {
          element.hidden = !showActions;
        });

        if (!variant) return;

        const variantInput = cardForm.querySelector('input.product-variant-id');
        if (variantInput) {
          variantInput.value = variant.id;
          variantInput.disabled = variant.available === false;
        }

        const atcButton = cardForm.querySelector('button[type="submit"][name="add"]');
        if (!atcButton) return;

        atcButton.disabled = variant.available === false;
      }

      async #onSubmit(event) {
        event.preventDefault();

        const emailInput = this.querySelector('[data-back-in-stock-email]');
        if (!emailInput || !this.submitButton) return;

        const email = emailInput.value.trim();
        if (!email) return;

        this.#setLoading(true);
        this.#hideMessages();

        const payload = {
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email,
                    locale: this.dataset.locale,
                  },
                },
              },
              channels: ['EMAIL'],
            },
            relationships: {
              variant: {
                data: {
                  type: 'catalog-variant',
                  id: `$shopify:::$default:::${this.dataset.variantId}`,
                },
              },
            },
          },
        };

        try {
          const response = await fetch(
            `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${this.dataset.companyId}`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                revision: '2024-06-15',
              },
              body: JSON.stringify(payload),
            }
          );

          if (response.status === 202 || response.status === 200) {
            this.form?.classList.add('hidden');
            if (this.successMessage) {
              this.successMessage.hidden = false;
            }
            return;
          }

          this.#showError();
        } catch {
          this.#showError();
        } finally {
          this.#setLoading(false);
        }
      }

      #setLoading(isLoading) {
        if (!this.submitButton) return;
        this.submitButton.disabled = isLoading;
        this.submitButton.classList.toggle('btn--loading', isLoading);
      }

      #hideMessages() {
        if (this.successMessage) this.successMessage.hidden = true;
        if (this.errorMessage) this.errorMessage.hidden = true;
      }

      #showError() {
        if (this.errorMessage) {
          this.errorMessage.hidden = false;
        }
      }
    }
  );
}
